import { FC, useEffect, useState } from 'react';
import { GridColDef, GridSortModel } from '@mui/x-data-grid';
import CustomNoRowsOverlay from 'src/components/StyledComponents/CustomEmptyTableRow';
import { Discount } from 'src/models/Management.interface';
import LoadingTable from 'src/components/StyledComponents/CustomLoadingTable';
import StyledDataGrid from 'src/components/StyledComponents/StyledDataGrid';
import StyledGridContainer from 'src/components/StyledComponents/StyledGridContainer';
import StyledSnackbar from 'src/components/StyledComponents/StyledSnackBar';
import { useGetDiscount } from 'src/services/discount.service';
import DiscountActionMenuButton from 'src/components/ActionButtons/DiscountActionMenu';
import PaginationControl from 'src/components/StyledComponents/StyledPagination';

const DiscountGrid: FC<{ state: boolean; searchTerm: string }> = ({
  state,
  searchTerm,
}) => {
  const { showSnackbar } = StyledSnackbar();
  const [page, setPage] = useState<number>(1);
  const [sortBy, setSortBy] = useState<string>('name');
  const [order, setOrder] = useState<'ASC' | 'DESC'>('ASC');
  const [sortModel, setSortModel] = useState<GridSortModel>([
    { field: 'name', sort: 'asc' },
  ]);
  const [pageSize, setPageSize] = useState<number>(15);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const { loading, discounts, fetchDiscounts, deleteDiscount, totalItems } =
    useGetDiscount();

  useEffect(() => {
    fetchDiscounts(page, pageSize, searchTerm, sortBy, order);
  }, [state, page, pageSize, searchTerm, sortBy, order, refreshTrigger]);

  useEffect(() => {
    setPage(1);
  }, [searchTerm]);

  useEffect(() => {
    setPage(1);
  }, [state]);

  const resetPageAndRefresh = () => {
    if (page === 1) {
      setRefreshTrigger((prev) => prev + 1);
    } else {
      setPage(1);
    }
  };

  const handleSortModelChange = (newSortModel: GridSortModel) => {
    setSortModel(newSortModel);
    const newOrder = (newSortModel[0]?.sort || 'asc').toUpperCase();
    const newSortBy = newSortModel[0]?.field || 'name';
    setSortBy(newSortBy);
    setOrder(newOrder as 'ASC' | 'DESC');
    setPage(1);
  };

  const columns: GridColDef[] = [
    {
      field: 'number',
      headerName: '#',
      flex: 0.3,
      headerAlign: 'center',
      align: 'center',
      sortable: false,
    },
    { field: 'name', headerName: 'Name', flex: 1 },
    {
      field: 'description',
      headerName: 'Description',
      flex: 2,
      sortable: false,
    },
    {
      field: 'percentage',
      headerName: 'Percentage',
      flex: 0.5,
      align: 'right',
      headerAlign: 'right',
    },
    {
      field: 'defaultValue',
      headerName: 'Default Value',
      flex: 0.75,
      align: 'right',
      headerAlign: 'right',
    },
    {
      field: 'action',
      headerName: 'Action',
      flex: 0.5,
      headerAlign: 'center',
      align: 'center',
      sortable: false,
      renderCell: (params) => (
        <DiscountActionMenuButton
          rowId={params.id}
          name={params.row.name}
          status={params.row.status}
          resetPageAndRefresh={resetPageAndRefresh}
          fetchDiscounts={() =>
            fetchDiscounts(page, pageSize, searchTerm, sortBy, order)
          }
          deleteDiscount={deleteDiscount}
        />
      ),
    },
  ];

  const transformData = (discounts: Discount[]) => {
    try {
      return discounts.map((discount, index) => ({
        id: discount.id,
        number: (page - 1) * pageSize + index + 1,
        name: discount.name,
        description: discount.description,
        percentage: discount.percentage ? `${discount.percentage}%` : '-',
        defaultValue: discount.defaultValue
          ? new Intl.NumberFormat('en-US', {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            }).format(discount.defaultValue)
          : '-',
      }));
    } catch (error) {
      showSnackbar('Error transforming discount data', 'error');
      return [];
    }
  };

  const rows = transformData(discounts);

  return loading ? (
    <LoadingTable columns={columns} />
  ) : (
    <StyledGridContainer
      pagination={
        totalItems > 0 ? (
          <PaginationControl
            totalItems={totalItems}
            page={page}
            setPage={setPage}
            pageSize={pageSize}
            setPageSize={setPageSize}
            loading={loading}
          />
        ) : undefined
      }
    >
      <StyledDataGrid
        rows={rows}
        columns={columns}
        disableColumnMenu
        slots={{
          noRowsOverlay: CustomNoRowsOverlay,
        }}
        sortModel={sortModel}
        onSortModelChange={handleSortModelChange}
        sortingOrder={['asc', 'desc']}
        sortingMode="server"
      />
    </StyledGridContainer>
  );
};

export default DiscountGrid;
